/**
 * @file TimePicker 时间选择
 * @description 时:分 选择；自定义浮层；值格式 `HH:mm`。
 *
 * @whenToUse 营业时间、提醒时刻等仅需时分。
 * @whenNotToUse 含日期（用 DateTimePicker）；日期区间（用 DateRangePicker）。
 */

import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Clock } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

export type TimePickerProps = {
  /** `HH:mm` */
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
  /** 分钟步进，默认 5 */
  minuteStep?: number
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function parseTime(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (hour > 23 || minute > 59) return null
  return { hour, minute }
}

export function TimePicker({
  value,
  onChange,
  placeholder = '选择时间',
  disabled = false,
  invalid = false,
  className,
  id,
  minuteStep = 5,
}: TimePickerProps) {
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const parsed = value ? parseTime(value) : null
  const [hour, setHour] = useState(parsed?.hour ?? 9)
  const [minute, setMinute] = useState(parsed?.minute ?? 0)

  useEffect(() => {
    if (parsed) {
      setHour(parsed.hour)
      setMinute(parsed.minute)
    }
  }, [value])

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const minutes = Array.from(
    { length: Math.floor(60 / minuteStep) },
    (_, i) => i * minuteStep,
  )

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, 200))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const apply = (h: number, m: number) => {
    onChange?.(`${pad(h)}:${pad(m)}`)
  }

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'inline-flex h-9 w-full items-center justify-between gap-2 rounded-lg border-2 bg-raised px-3 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            value ? 'text-foreground' : 'text-muted',
          )}
        >
          {value || placeholder}
        </span>
        <Clock className="size-4 shrink-0 text-muted" aria-hidden />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              <div className="flex">
                <ScrollArea className="h-48 w-1/2 border-r border-border">
                  <div className="p-1">
                    {hours.map((h) => (
                      <button
                        key={h}
                        type="button"
                        className={cn(
                          'flex h-9 w-full items-center justify-center rounded-md text-sm transition-opacity duration-200 hover:opacity-90',
                          h === hour ? 'bg-primary/15 text-primary' : 'text-foreground',
                        )}
                        onClick={() => {
                          setHour(h)
                          apply(h, minute)
                        }}
                      >
                        {pad(h)}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
                <ScrollArea className="h-48 w-1/2">
                  <div className="p-1">
                    {minutes.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={cn(
                          'flex h-9 w-full items-center justify-center rounded-md text-sm transition-opacity duration-200 hover:opacity-90',
                          m === minute
                            ? 'bg-primary/15 text-primary'
                            : 'text-foreground',
                        )}
                        onClick={() => {
                          setMinute(m)
                          apply(hour, m)
                          setOpen(false)
                        }}
                      >
                        {pad(m)}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
